/**
 * probeFlag — 진단 화면의 계측 오버레이를 켜는 스위치.
 *
 * **왜 URL 질의냐**: 켜야 하는 사람은 실기기 앞에 있고, 그 기기에서 코드를 못 고친다.
 * 앱은 딥링크를 WebView 로 넘기므로(`WebViewScreen`) `…/?probe=1` 한 번이면 켜진다.
 * `import.meta.env.DEV` 는 못 쓴다 — 재현되는 곳이 프로덕션 빌드다.
 *
 * **왜 저장하냐**: 진단은 여러 화면을 지나며 내부 라우팅을 타서 질의가 사라진다.
 * 한 번 켜면 끌 때까지(`?probe=0`) 남는다.
 *
 * 저장은 실패해도 무해하다 — 못 읽으면 그냥 꺼진 것이다(사파리 프라이빗 등).
 */

import { IS_DEV } from '@muzly/shared';

const STORAGE_KEY = 'muzly.captureProbe';
const PARAM = 'probe';

const ON = new Set(['1', 'on', 'true']);
const OFF = new Set(['0', 'off', 'false']);

/**
 * 질의 문자열이 스위치를 **지시하는가**. 지시가 없으면 `null` — "끄라"와 다르다.
 * (없음을 false 로 읽으면 링크를 한 번 탄 뒤 다음 화면에서 저절로 꺼진다.)
 */
export const probeFromQuery = (search: string): boolean | null => {
  const raw = new URLSearchParams(search).get(PARAM);
  if (raw == null) {
    return null;
  }
  const value = raw.toLowerCase();
  if (ON.has(value)) {
    return true;
  }
  return OFF.has(value) ? false : null;
};

/**
 * 저장된 지시. **3상이다** — `true`·`false`·`null`(지시 없음).
 *
 * dev 기본이 ON 이므로 "끔"도 저장돼야 한다: 두 값을 안 가르면 `?probe=0` 이 그 화면에서만
 * 살고 질의 없는 다음 로드에서 다시 켜진다.
 */
const read = (): boolean | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) {
      return null;
    }
    return raw === '1';
  } catch {
    return null;
  }
};

const write = (on: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* 저장 못 하면 이번 화면에서만 산다 */
  }
};

/**
 * 지금 켜져 있나. 질의에 지시가 있으면 그것이 이기고, 그 값을 저장한다.
 *
 * **dev 빌드에서는 지시가 없으면 켠다**(2026-09-16). 앱 WebView 는 `ENV.WEB_URL` 을
 * 질의 없이 싣고(`WebViewScreen.tsx:373`), 그 안에서 질의를 붙이거나 `localStorage` 를
 * 건드리려면 Safari 인스펙터를 붙여야 한다 — 실기기 앞의 사람에게 그 단계를 요구하면
 * 계측이 거기서 멈춘다. prod(`IS_DEV=false`)는 그대로 꺼져 있고, dev 에서도 `?probe=0`
 * 으로 끌 수 있다.
 */
export const isProbeOn = (): boolean => {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const asked = probeFromQuery(search);
  if (asked == null) {
    // 질의 > 저장된 지시 > 빌드 기본. 저장된 OFF 가 dev 기본을 이긴다 — 안 그러면
    // `?probe=0` 이 다음 로드에서 되살아난다.
    return read() ?? IS_DEV;
  }
  write(asked);
  return asked;
};
